// src/pages/Admin/Appointment/AllPetRadiology.jsx
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaTimes, FaBell, FaCheckCircle, FaBan, FaClock, FaUserMd, FaPaw, FaXRay } from "react-icons/fa";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function AllPetRadiology() {
    const { t } = useContext(TranslatorContext);

    const [petRadiologies, setPetRadiologies] = useState([]);
    const [loading, setLoading] = useState(true);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Status Modal States
    const [statusModal, setStatusModal] = useState(false);
    const [selectedRadiology, setSelectedRadiology] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        customer_name: '',
        pet_name: '',
        start_date: '',
        end_date: '',
        reminder_status: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchPetRadiologies();
    }, [currentPage, filters]);

    const fetchPetRadiologies = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams({
            page: currentPage,
            ...(filters.customer_name && { customer_name: filters.customer_name }),
            ...(filters.pet_name && { pet_name: filters.pet_name }),
            ...(filters.start_date && { start_date: filters.start_date }),
            ...(filters.end_date && { end_date: filters.end_date }),
            ...(filters.reminder_status && { reminder_status: filters.reminder_status })
        });

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/all-pet-radiology?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPetRadiologies(response.data.petRadiologies.data || []);
                setLastPage(response.data.petRadiologies.last_page || 1);
                setTotal(response.data.petRadiologies.total || 0);
                setPerPage(response.data.petRadiologies.per_page || 10);
            }
        } catch (error) {
            console.error("Error fetching pet radiologies:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load radiologies',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            customer_name: '',
            pet_name: '',
            start_date: '',
            end_date: '',
            reminder_status: ''
        });
        setCurrentPage(1);
    };

    const openStatusModal = (radiology) => {
        setSelectedRadiology(radiology);
        setSelectedStatus(radiology.status || '');
        setStatusModal(true);
    };

    const handleStatusUpdate = async () => {
        if (!selectedStatus) {
            Swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: 'Please select a status',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        setUpdatingStatus(true);
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-radiology-update-riminder/${selectedRadiology.id}`,
                { reminder_status: selectedStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setStatusModal(false);
                setSelectedRadiology(null);
                setSelectedStatus('');
                fetchPetRadiologies();

                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Reminder status updated successfully',
                    timer: 2000,
                    showConfirmButton: true,
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error updating status:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update status',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            completed: { class: 'mc-badge mc-badge-success', text: 'Completed', icon: <FaCheckCircle /> },
            missed: { class: 'mc-badge mc-badge-danger', text: 'Missed', icon: <FaBan /> },
            cancelled: { class: 'mc-badge mc-badge-warning', text: 'Cancelled', icon: <FaTimes /> },
            pending: { class: 'mc-badge mc-badge-info', text: 'Pending', icon: <FaClock /> }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <span className={config.class}>{config.icon} {config.text}</span>;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;

            const formattedDate = `${day}-${month}-${year}`;
            const formattedTime = `${hours}:${minutes} ${ampm}`;

            return `${formattedDate} ${formattedTime}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && currentPage === 1) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Pet Radiologies</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Appointments</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Radiologies</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-body">
                            <Row>
                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">Customer Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={filters.customer_name}
                                        onChange={handleFilterChange}
                                        placeholder="Search by customer"
                                    />
                                </Col>

                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">Pet Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pet_name"
                                        value={filters.pet_name}
                                        onChange={handleFilterChange}
                                        placeholder="Search by pet"
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        value={filters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        value={filters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">Reminder Status</Form.Label>
                                    <Form.Select
                                        name="reminder_status"
                                        value={filters.reminder_status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="pending">Pending</option>
                                        <option value="completed">Completed</option>
                                        <option value="missed">Missed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12} className="d-flex justify-content-end">
                                    <ButtonComponent
                                        className="btn btn-secondary"
                                        onClick={clearFilters}
                                    >
                                        <FaTimes className="me-1" /> Clear Filters
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">Radiologies List</h5>
                            <span className="mc-badge mc-badge-info">
                                Total: {total}
                            </span>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>Appointment Number</th>
                                                    <th>Pet Name</th>
                                                    <th>Radiology Type</th>
                                                    <th>Radiology Date</th>
                                                    <th>Doctor</th>
                                                    <th>Reminder Date</th>
                                                    <th>Reminder Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {petRadiologies.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center">
                                                            No radiologies found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    petRadiologies.map((radiology, index) => (
                                                        <tr key={radiology.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{(currentPage - 1) * perPage + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="mc-badge mc-badge-primary">
                                                                    {radiology.appointment?.appointment_number || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <strong className="d-flex align-items-center gap-2">
                                                                        <FaPaw className="text-primary" />
                                                                        {radiology.appointment?.pet?.name || 'N/A'}
                                                                    </strong>
                                                                    <small className="text-muted">
                                                                        Owner: {radiology.appointment?.customer?.name || 'N/A'}
                                                                    </small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FaXRay className="text-info" />
                                                                    {radiology.radiology?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {formatDateTime(radiology.radiology_date)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <FaUserMd className="text-secondary" />
                                                                    {radiology.appointment?.doctor?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <FaBell className="text-warning" />
                                                                        {formatDateTime(radiology.reminder_date)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{getStatusBadge(radiology.status)}</td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Pet radiology edit") && (

                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => openStatusModal(radiology)}
                                                                            title="Update status"
                                                                        >
                                                                            edit_note
                                                                        </ButtonComponent>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Pagination */}
                                        {lastPage > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top px-3 pb-3">
                                                <div className="text-muted small">
                                                    Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, total)} of {total} entries
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </ButtonComponent>
                                                    <span className="px-3 py-1 bg-light rounded">
                                                        Page {currentPage} of {lastPage}
                                                    </span>
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === lastPage}
                                                    >
                                                        Next
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Status Update Modal */}
            <Modal
                show={statusModal}
                onHide={() => setStatusModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        <FaBell className="me-2" />
                        Update Radiology Status
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Modal.Body className="custom-modal-body">
                        {selectedRadiology && (
                            <>
                                <div className="alert alert-info mb-3">
                                    <div><strong>Appointment:</strong> #{selectedRadiology.appointment?.appointment_number}</div>
                                    <div><strong>Pet:</strong> {selectedRadiology.appointment?.pet?.name}</div>
                                    <div><strong>Radiology:</strong> {selectedRadiology.radiology?.name || 'N/A'}</div>
                                    <div><strong>Radiology Date:</strong> {formatDateTime(selectedRadiology.radiology_date)}</div>
                                    <div><strong>Reminder Date:</strong> {formatDateTime(selectedRadiology.reminder_date)}</div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        Reminder Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                        className="custom-input"
                                    >
                                        <option value="">Select status</option>
                                        <option value="completed">Completed</option>
                                        <option value="missed">Missed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </Form.Select>
                                </Form.Group>

                                <div className="alert alert-warning mt-3">
                                    <FaTimes className="me-2" />
                                    <small>Changing the status will update the reminder status for this radiology.</small>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStatusModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handleStatusUpdate}
                            disabled={updatingStatus}
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                        >
                            {updatingStatus && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update Status
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style jsx>{`
                .custom-modal-dialog {
                    max-width: 500px;
                }
                .custom-modal-content {
                    border-radius: 12px;
                }
                .custom-modal-header {
                    background-color: #d7d8da;
                    border-bottom: none;
                }
                .custom-modal-header .btn-close {
                    filter: brightness(0) invert(1);
                }
                .custom-modal-title {
                    font-weight: 600;
                }
                .custom-modal-body {
                    padding: 20px;
                }
                .custom-modal-footer {
                    border-top: none;
                    padding: 15px 20px;
                }
                .custom-input:focus {
                    border-color: #3182ce;
                    box-shadow: 0 0 0 0.2rem rgba(8, 88, 247, 0.25);
                }
                .mc-table-action {
                    display: flex;
                    gap: 8px;
                }
                .mc-table-action .material-icons {
                    cursor: pointer;
                    font-size: 20px;
                }
                .mc-table-action .material-icons.edit {
                    color: #3182ce;
                }
                .mc-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .mc-badge-success {
                    background-color: #d4edda;
                    color: #155724;
                }
                .mc-badge-danger {
                    background-color: #f8d7da;
                    color: #721c24;
                }
                .mc-badge-warning {
                    background-color: #fff3cd;
                    color: #856404;
                }
                .mc-badge-info {
                    background-color: #d1ecf1;
                    color: #0c5460;
                }
                .mc-badge-primary {
                    background-color: #cfe2ff;
                    color: #084298;
                }
            `}</style>
        </PageLayout>
    );
}